import { getUserId } from '@/utils/helpers/cookies.helpers';

const MIA_LOG_URL = import.meta.env.VITE_MIA_CONSOLE_LOG_URL as string | undefined;

type Level = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';
type LogType = 'log' | 'http' | 'webhook';
type LogOpts = { sensitiveFields?: string[]; type?: LogType };

const SENSITIVE_KEYS = new Set([
  'authorization', 'api-token', 'apikey', 'apitoken', 'password', 'passcode',
  'pin', 'token', 'accesstoken', 'refreshtoken', 'idtoken', 'clientsecret',
]);

function detectSensitiveFields(obj: unknown, prefix = ''): string[] {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  const found: string[] = [];
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (SENSITIVE_KEYS.has(k.toLowerCase()) && v != null) {
      found.push(path);
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      found.push(...detectSensitiveFields(v, path));
    }
  }
  return found;
}

async function sendWithRetry(body: string, attempt: number): Promise<void> {
  try {
    const res = await fetch(MIA_LOG_URL!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    if (res.ok || attempt >= 9) return;
  } catch {
    if (attempt >= 9) return;
  }
  await new Promise<void>(r => setTimeout(r, 10_000));
  return sendWithRetry(body, attempt + 1);
}

export function miaConsoleLog(
  level: Level,
  data: Record<string, unknown>,
  opts?: LogOpts,
): void {
  if (!MIA_LOG_URL) return;

  const autoFields = detectSensitiveFields(data);
  const allSensitiveFields = [...new Set([...(opts?.sensitiveFields ?? []), ...autoFields])];

  const userId = getUserId() ?? undefined;
  const payload: Record<string, unknown> = {
    source: 'gifting',
    level,
    ...(userId ? { userId } : {}),
    data,
    source_timestamp: new Date().toISOString(),
    ...(opts?.type ? { type: opts.type } : {}),
  };
  if (allSensitiveFields.length) payload['sensitive_fields'] = allSensitiveFields;
  sendWithRetry(JSON.stringify(payload), 0).catch(() => {});
}
